<template>
  <div>
    <div>
      <!-- ===== STEP 1: Plan Questions (general individual only, hidden after Next clicked) ===== -->
      <div class="bg-white border border-border rounded-md lg:mb-5 mb-4"
        v-if="state.opt_type == 'general' && state.enroll_option == 'individual'">

        <!-- Q1: Do You Have A Commercial Driver's License? -->
        <div class="flex w-full !m-0 p-4 gap-[8px] lg:items-center flex-col lg:flex-row items-start border-b border-border">
          <div class="flex xl:items-center gap-[8px] w-full items-baseline">
            <span class="number-badge shrink-0">1</span>
            <h2 class="card-title !m-0 flex items-center flex-wrap gap-2">
              Do You Have A Commercial Driver's License (CDL)?
              <div class="info-wrapper relative inline-block group">
                <span class="info-icon">i</span>
                <div class="tooltip-box hidden group-hover:block">
                  <div class="tooltip-title">What is a Commercial Driver's License (CDL)?</div>
                  <div class="tooltip-body">
                    You have a CDL if you are licensed to drive large or specialized vehicles like
                    tractor-trailers, buses, or vehicles carrying hazardous materials.<br><br>
                    If your license is a standard passenger license for personal vehicles
                    (cars, SUVs, pickup trucks), you do not have a CDL.
                  </div>
                  <div class="tooltip-arrow"></div>
                </div>
              </div>
            </h2>
          </div>
          <fieldset class="flex items-center gap-[16px] ms-[27px] lg:ms-0 shrink-0">
            <legend class="sr-only">Commercial Driver's License (CDL)</legend>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.hasCDL === 'yes'"
                  @change="onHasCDLChange('yes')" name="hasCDL" value="yes" />
                <span>Yes</span><span class="checkmark"></span>
              </label>
            </div>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.hasCDL === 'no'"
                  @change="onHasCDLChange('no')" name="hasCDL" value="no" />
                <span>No</span><span class="checkmark"></span>
              </label>
            </div>
          </fieldset>
        </div>

        <!-- Q2: Which plans are you interested in? (only when CDL = Yes) -->
        <div v-if="state.hasCDL === 'yes'"
          class="flex w-full !m-0 p-4 gap-[8px] lg:items-center flex-col lg:flex-row items-start border-b border-border">
          <div class="flex xl:items-center gap-[8px] w-full items-baseline">
            <span class="number-badge shrink-0">2</span>
            <h2 class="card-title !m-0">Which plans are you interested in?</h2>
          </div>
          <fieldset class="flex flex-wrap items-center gap-[16px] ms-[27px] lg:ms-0 shrink-0">
            <legend class="sr-only">Plan interest</legend>
            <div v-for="option in planInterestOptions" :key="option" class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.planInterest === option"
                  @change="onPlanInterestChange(option)" name="planInterest" :value="option" />
                <span>{{ option }}</span><span class="checkmark"></span>
              </label>
            </div>
          </fieldset>
        </div>

        <!-- Q3: Who are you buying a plan for? -->
        <!-- Shown when CDL=No, or CDL=Yes and planInterest is set and not CDL-only -->
        <div v-if="showWhoFor"
          class="flex w-full !m-0 p-4 gap-[8px] lg:items-center flex-col lg:flex-row items-start border-b border-border">
          <div class="flex xl:items-center gap-[8px] w-full items-baseline">
            <span class="number-badge shrink-0">{{ state.hasCDL === 'yes' ? '3' : '2' }}</span>
            <h2 class="card-title !m-0">Who are you buying a plan for?</h2>
          </div>
          <fieldset class="flex flex-wrap items-center gap-[16px] ms-[27px] lg:ms-0 shrink-0">
            <legend class="sr-only">Plan purchase for</legend>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.whoFor === 'Yourself'"
                  @change="$emit('updateState', { key: 'whoFor', value: 'Yourself' })"
                  name="whoFor" value="Yourself" />
                <span>Yourself</span><span class="checkmark"></span>
              </label>
            </div>
            <div class="field-radio-block">
              <label class="radio radio-outline-primary field-radio !mb-0">
                <input type="radio" :checked="state.whoFor === 'You and your family'"
                  @change="$emit('updateState', { key: 'whoFor', value: 'You and your family' })"
                  name="whoFor" value="You and your family" />
                <span>You and your family</span><span class="checkmark"></span>
              </label>
            </div>
          </fieldset>
        </div>

      </div>

      <!-- SEIU / Affiliated union section (unchanged) -->
      <div class="bg-white border border-border rounded-md lg:mb-5 mb-4"
        v-if="(state.enroll_option == 'affilliated' && state.opt_type == 'general' || state.opt_type == 'SEIU721')">
        <div class="flex w-full border-b border-border !m-0 p-4 gap-[8px] items-center">
          <span class="number-badge">1</span>
          <h2 class="card-title !m-0">Union Member Information</h2>
        </div>
        <div class="p-4">
          <div class="form-group mb-4 flex items-center justify-start gap-[16px]">
            <span id="isSEIU-label" class="font-normal text-mute text-sm mb-0 inline-block">
              Are you a SEIU721 Union Member?<span class="text-error">*</span>
            </span>
            <fieldset class="flex items-center gap-[20px]" role="radiogroup" aria-labelledby="isSEIU-label">
              <legend class="sr-only">SEIU721 Union Member</legend>
              <div class="field-radio-block">
                <label class="radio radio-outline-primary field-radio !mb-0">
                  <input type="radio" :checked="state.isSEIU === 'yes'"
                    @change="$emit('updateState', { key: 'isSEIU', value: 'yes' })" name="isSEIU"
                    value="yes" /><span>Yes</span><span class="checkmark"></span>
                </label>
              </div>
              <div class="field-radio-block">
                <label class="radio radio-outline-primary field-radio !mb-0">
                  <input type="radio" name="isSEIU" @change="$emit('updateState', { key: 'isSEIU', value: 'no' })"
                    value="no" /><span>No</span><span class="checkmark"></span>
                </label>
              </div>
            </fieldset>
          </div>
          <div v-if="state.isSEIU === 'no'" class="bg-white border border-border rounded-md lg:mb-5 mb-4">
            <div class="text-center p-4 md:w-[400px] w-auto mx-auto">
              <span
                class="w-[60px] h-[60px] rounded-full flex items-center justify-center border bg-[#fff2f2] border-border mx-auto mb-4">
                <img src="@/assets/images/warning-info.svg" alt="info" />
              </span>
              <h6 class="font-bold text-blackPrimary text-lg">We're Sorry</h6>
              <p class="content !m-0">
                To confirm Union Membership or to become a Member of the Union, please contact a SEIU721 Membership
                Analyst at membership@SEIU721.org or call (213) 251-3757. You may also click the following link to
                become a member or verify membership <u class="text-blue-500"><a
                    href="https://www.seiu721.org/create-account.php"
                    target="_blank">https://www.seiu721.org/create-account.php</a></u>
              </p>
            </div>
          </div>
          <div class="form-group" v-if="state.isSEIU == 'yes' && state.isFAM == 'yes'">
            <label class="font-normal text-mute text-sm mb-2 inline-block">
              Select Company<span class="text-error">*</span>
            </label>
            <select id="company" name="company" v-model="selectedCompany" @change="updateSelectedCompany"
              class="form-mask">
              <option v-for="company in state.companies" :key="company.id" :value="company">
                {{ company.company_name }}
              </option>
            </select>
            <small class="mt-[8px] block text-mute text-xs font-normal">
              If you do not see your employer in this drop-down list, please contact a SEIU721 Membership Analyst at
              membership@SEIU721.org or call (213) 251-3757 to confirm if you are able to enroll.
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    state: Object,
  },
  data() {
    return {
      selectedCompany: null,
      showNextError: false,
    };
  },
  computed: {
    // Derive Q2 options dynamically from bundled_group when plans are loaded,
    // else fall back to the default set. When Family Defender (or other types)
    // are added to the DB, they will appear here automatically.
    planInterestOptions() {
      const plans = this.state.bundled_group;
      if (!plans || plans.length === 0) {
        // Default before API call — will be validated/filtered after address step
        return ['CDL', 'Identity Defender', 'Both'];
      }
      const isBundle = (p) => String(p.plan_id).includes(',');
      const isCDLPlan = (p) => p.planType === 'CDL';
      const options = [];
      if (plans.some(p => isCDLPlan(p) && !isBundle(p))) options.push('CDL');
      if (plans.some(p => !isCDLPlan(p) && !isBundle(p))) options.push('Identity Defender');
      if (plans.some(p => isBundle(p))) options.push('Both');
      return options.length > 0 ? options : ['CDL', 'Identity Defender', 'Both'];
    },

    // Show Q3 (who for) when:
    // CDL=No (always show Q3 for ID plans), OR
    // CDL=Yes and planInterest is set and not CDL-only
    showWhoFor() {
      if (this.state.hasCDL === 'no') return true;
      if (this.state.hasCDL === 'yes' && this.state.planInterest && this.state.planInterest !== 'CDL') return true;
      return false;
    },

    // Whether all required questions are answered
    canProceed() {
      const hasCDL = this.state.hasCDL;
      const planInterest = this.state.planInterest;
      const whoFor = this.state.whoFor;
      if (!hasCDL) return false;
      if (hasCDL === 'yes') {
        if (!planInterest) return false;
        // CDL-only: no Q3 needed
        if (planInterest === 'CDL') return true;
        // Identity Defender or Both: need Q3
        return !!whoFor;
      }
      // CDL = No: need Q3
      return !!whoFor;
    },
  },
  methods: {
    updateSelectedCompany() {
      this.$emit('updateState', { key: 'company', value: this.selectedCompany });
      this.$emit('updateState', { key: 'company_number', value: this.selectedCompany.company });
    },

    onHasCDLChange(value) {
      this.$emit('updateState', { key: 'hasCDL', value });
      // Reset downstream selections when CDL answer changes
      this.$emit('updateState', { key: 'planInterest', value: '' });
      this.$emit('updateState', { key: 'whoFor', value: '' });
      this.$emit('updateState', { key: 'disp_enroll_section', value: false });
      this.showNextError = false;
    },

    onPlanInterestChange(value) {
      this.$emit('updateState', { key: 'planInterest', value });
      // Reset Q3 when plan interest changes
      this.$emit('updateState', { key: 'whoFor', value: '' });
      this.showNextError = false;
    },

    goToStep2() {
      if (!this.canProceed) {
        this.showNextError = true;
        return;
      }
      this.showNextError = false;
      this.$emit('updateState', { key: 'plan_questions_complete', value: true });
    },
  }
};
</script>

<style scoped>
.info-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 18px;
  height: 18px;
  border: 1.5px solid #b23f22;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  color: #b23f22;
  line-height: 1;
  text-transform: lowercase;
}

.tooltip-box {
  position: absolute;
  top: -40px;
  left: 40px;
  width: 300px;
  background: #f8fdfd;
  border: 1.5px solid #1a1a1a;
  border-radius: 6px;
  z-index: 50;
  color: #333;
  box-shadow: 0 4px 10px rgba(0,0,0,0.12);
  overflow: hidden;
}

.tooltip-title {
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
  color: #1a1a1a;
  padding: 12px;
  background-color: #d9f9f9;
}

.tooltip-body {
  font-size: 13px;
  line-height: 18px;
  color: #1a1a1a;
  padding: 12px;
  font-weight: 500;
  background-color: #ffffff;
}

.tooltip-arrow {
  display: none;
  position: absolute;
  top: -8px;
  left: 30px;
  width: 14px;
  height: 14px;
  background: #f8fdfd;
  border-top: 1.5px solid #b9e3df;
  border-left: 1.5px solid #b9e3df;
  transform: rotate(45deg);
}

@media (max-width: 991px) {
  .tooltip-box {
    left: auto;
    right: 0;
    top: 40px;
    width: min(300px, calc(100vw - 32px));
  }
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
